import { z } from 'zod';
import { ClassIdSchema, MapIdSchema, TowerIdSchema } from './common.js';
import { SynergyEffectSchema } from './effects.js';

// ── Meta unlock tree (TRD-08 §8) ──────────────────────────────────────────────────────

/**
 * One node of the unlock tree (R-08-18).
 *
 * A DAG with `requires`, not a flat shop — prerequisites are what make it read as a
 * tree and what gate the exotic classes behind the simple ones. `position` is layout
 * data for the tree screen (TRD-09 R-09-22) and means nothing to the game rules.
 */
export const UnlockNodeSchema = z.object({
  id: z
    .string()
    .min(2)
    .regex(/^[a-z0-9-]+$/, 'node ids are kebab-case'),
  kind: z.enum(['tower', 'class', 'map', 'start', 'effect']),
  /** Shown on the node and in the hover panel. */
  name: z.string().min(2).max(40),
  /** One sentence: what buying this actually does. The hover panel's main text. */
  summary: z.string().min(8).max(160),
  /** Shards. The whole tree must cost ≤ 60 (R-08-20, V-13). */
  cost: z.number().int().positive(),
  /** ALL must be owned before this node can be bought. */
  requires: z.array(z.string()).default([]),
  payload: z.discriminatedUnion('k', [
    z.object({ k: z.literal('tower'), towerId: TowerIdSchema }),
    /** Unlocks the class AND its 6 towers. */
    z.object({ k: z.literal('class'), classId: ClassIdSchema }),
    z.object({ k: z.literal('map'), mapId: MapIdSchema }),
    z.object({
      k: z.literal('start'),
      stat: z.enum(['gold', 'benchSlot', 'coreHp', 'placement']),
      value: z.number().int().positive(),
    }),
    /**
     * A gameplay modifier applied to free-play runs at synergy recompute, riding the
     * same `RunEffect` seam as the daily mutator (R-08-22b) — zero new evaluation code.
     * `forClass` scopes it to one class's placed towers; omitted, it is global.
     */
    z.object({
      k: z.literal('effect'),
      forClass: ClassIdSchema.optional(),
      effects: z.array(SynergyEffectSchema).min(1),
    }),
  ]),
  /** Abstract layout coordinates, 0–100 on both axes. */
  position: z.object({ x: z.number().min(0).max(100), y: z.number().min(0).max(100) }),
});

export type UnlockNode = z.infer<typeof UnlockNodeSchema>;
